import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import { FC, Fragment, KeyboardEvent, memo, MouseEvent, useMemo } from 'react';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

import { useDropPreviewIndex } from './BoardInteractionContext';
import { DispatchCardView } from './DispatchCardView';
import { DisplayMetric } from './DisplayMetricToggle';

export type VehicleRowCardsProps = {
  /** ドロップ位置プレビューの購読キー。自分の行に来たプレビューだけを読む */
  rowId: string;
  vehicle: Vehicle;
  cards: DispatchCard[];
  duplicateStoreNames?: Set<string>;
  routeDeviationCardIds?: Set<string>;
  geoAlerts?: Map<string, GeoAlertInfo>;
  displayMetrics?: Set<DisplayMetric>;
  onSelectCard?: (
    card: DispatchCard,
    vehicle: Vehicle,
    orderIndex: number,
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => void;
};

export const VehicleRowCards: FC<VehicleRowCardsProps> = memo(({
  rowId,
  vehicle,
  cards,
  duplicateStoreNames,
  routeDeviationCardIds,
  geoAlerts,
  displayMetrics,
  onSelectCard,
}) => {
  // 他 row / 未割当からのドロップ予定 index。null なら未表示。
  // index === visibleCards.length の場合は末尾に縦バーを描画する。
  // 自分の行に来たプレビューだけを購読するので、他の行へのホバーではここは描き直さない
  // (同 row 内の並び替えは SortableContext がカードをスライドして可視化するので、
  // handleDragOver が同 row のプレビューを立てない)
  const dropPreviewIndex = useDropPreviewIndex(rowId);

  // 同 row 内で同じ card.id が居ると React duplicate key 警告 + dnd-kit 不具合になるため
  // 描画直前に重複除去する。設計上は上流 (vehicleRowVM / placeLegRunInRow) で起きないはずだが、
  // FR template と実 op の組み合わせ等エッジケースの安全網として保険を入れる。
  const visibleCards = useMemo(() => {
    const seen = new Set<string>();
    return cards.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [cards]);

  // useMemo で包む理由 (Why not 素の配列生成のまま):
  // SortableContext 内部の `items` useMemo は items prop (=このIDの配列) を参照で
  // 依存比較する (node_modules/@dnd-kit/sortable/dist/sortable.esm.js:308
  // `useMemo(() => userDefinedItems.map(...), [userDefinedItems])`)。
  // 中身が同じでも配列の参照が毎回変わると `items` も毎回新しくなり、それが
  // contextValue の useMemo の依存 (同ファイル 324-335 行目) に入っているため
  // Context.Provider の value が毎回変わり、この行の全カードの useSortable が
  // 連鎖的に再レンダーする。
  // (itemsHaveChanged は値ごとの比較 (itemsEqual, 同ファイル 40 行目) なので配列の
  // 参照が変わるだけでは true にならず、measureDroppableContainers はそもそも
  // isDragging 中しか呼ばれない (同ファイル 317 行目) — 参照非同一性のコストは
  // 再測定ではなく上記の再レンダー連鎖の方である)。
  const sortableIds = useMemo(
    () => visibleCards.filter((c) => c.type === 'delivery' && c.status !== 'no-delivery').map((c) => c.id),
    [visibleCards],
  );

  // Calculate total cases for loading cards
  const rowTotalCases = useMemo(
    () => visibleCards.reduce((sum, c) => {
      if (c.type === 'delivery' && c.status === 'matched' && c.todayCases !== null) return sum + c.todayCases;
      return sum;
    }, 0),
    [visibleCards],
  );

  let deliveryOrder = 0;

  return (
    <>
      <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
        {visibleCards.map((card, idx) => {
          const orderIndex = card.type === 'delivery' ? ++deliveryOrder : 0;
          const isDelivery = card.type === 'delivery';
          return (
            <Fragment key={card.id}>
              {dropPreviewIndex === idx && <DropIndicator />}
              <DispatchCardView
                card={card}
                showDuplicate={isDelivery && card.type === 'delivery' ? duplicateStoreNames?.has(card.storeName) ?? false : false}
                routeDeviation={routeDeviationCardIds?.has(card.id) ?? false}
                geoAlert={geoAlerts?.get(card.id)}
                totalCases={card.type === 'loading' ? rowTotalCases : undefined}
                displayMetrics={displayMetrics}
                vehicle={vehicle}
                orderIndex={orderIndex}
                onSelectCard={onSelectCard}
              />
            </Fragment>
          );
        })}
        {dropPreviewIndex === visibleCards.length && <DropIndicator />}
      </SortableContext>
    </>
  );
});

VehicleRowCards.displayName = 'VehicleRowCards';

/** ドロップ予定位置を示す青い縦バー (アニメーション付き) */
const DropIndicator: FC = () => (
  <Box
    data-testid="drop-indicator"
    sx={{
      width: 4,
      height: 56,
      flexShrink: 0,
      borderRadius: 1,
      bgcolor: 'hsl(var(--primary))',
      boxShadow: '0 0 8px hsl(var(--primary) / 0.6)',
      animation: 'dropIndicatorPulse 1s ease-in-out infinite',
      '@keyframes dropIndicatorPulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.5 },
      },
    }}
  />
);
